/* eslint-disable react-hooks/rules-of-hooks */
import { useRef, useEffect, PropsWithChildren } from "react"
import { useQuery } from "@blitzjs/rpc"
import { useParam } from "@blitzjs/next"
import { PaywallPreviewProducts } from "app/paywalls/helpers/types"
import { ParsedPaywall } from "app/paywalls/mutations/createPaywall"
import getPaywall from "app/paywalls/queries/getPaywall"
import {
  createContext,
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react"
import { Sub } from "@superwall-me/schema"
import { PageColor } from "../sections/ElementSection/components/PageStyles"
import { TPaywallTemplate } from "app/schema/paywallTemplate/paywallTemplate"

type PaywallContextType = {
  paywall: ParsedPaywall | undefined
  setQueryData: any // TODO Set this to the actual type
  refetchPaywall: () => Promise<unknown>
  paywallId?: number
  isSubstitutionFocused: boolean
  setIsSubstitutionFocused: Dispatch<SetStateAction<boolean>>
  selectedSubstitutionKey?: string
  setSelectedSubstitutionKey: Dispatch<SetStateAction<string | undefined>>
  substitutions?: Sub[] | [] | undefined
  setSubstitutions: Dispatch<SetStateAction<Sub[] | [] | undefined>>
  substitutionsFromPing?: Sub[] | [] | undefined
  setSubstitutionsFromPing: Dispatch<SetStateAction<Sub[] | [] | undefined>>
  productPreviewPayload?: PaywallPreviewProducts | null
  setProductPreviewPayload: Dispatch<SetStateAction<PaywallPreviewProducts | null>>
  hasError: boolean
  setHasError: Dispatch<SetStateAction<boolean>>
  pageColors?: PageColor[] | null
  setPageColors: Dispatch<SetStateAction<PageColor[] | null>>
  templateJson?: TPaywallTemplate | null
  setTemplateJson: Dispatch<SetStateAction<TPaywallTemplate | null>>
  iframeRef?: RefObject<HTMLIFrameElement | null>
  setIframeRef: Dispatch<SetStateAction<RefObject<HTMLIFrameElement | null>>>
  hoverSubstitutionKey?: string | null
  setHoveredSubstitutionKey: Dispatch<SetStateAction<string | null>>
  isInitializing: boolean
  setIsInitializing: Dispatch<SetStateAction<boolean>>
}

type Selector<K extends keyof PaywallContextType> = {
  [P in K]: true
}

export const PaywallContext = createContext<PaywallContextType>({
  paywall: undefined,
  setQueryData: () => {},
  refetchPaywall: () => Promise.resolve(),
  isSubstitutionFocused: false,
  setIsSubstitutionFocused: () => {},
  selectedSubstitutionKey: undefined,
  setSelectedSubstitutionKey: () => {},
  substitutions: undefined,
  setSubstitutions: () => {},
  substitutionsFromPing: undefined,
  setSubstitutionsFromPing: () => {},
  productPreviewPayload: null,
  setProductPreviewPayload: () => {},
  hasError: false,
  setHasError: () => {},
  pageColors: null,
  setPageColors: () => {},
  templateJson: null,
  setTemplateJson: () => {},
  iframeRef: undefined,
  setIframeRef: () => {},
  hoverSubstitutionKey: null,
  setHoveredSubstitutionKey: () => {},
  isInitializing: true,
  setIsInitializing: () => {},
})

export const PaywallProvider: FC<
  PropsWithChildren<{
    paywallId?: number
  }>
> = ({ children }) => {
  const paywallId = useParam("paywallId", "number")!
  const [paywall, { setQueryData, refetch }] = useQuery(
    getPaywall,
    { id: paywallId },
    {
      // This ensures the query never refreshes and overwrites the form data while the user is editing.
      staleTime: Infinity,
      suspense: false,
    }
  )

  const [iframeRef, setIframeRef] = useState<RefObject<HTMLIFrameElement | null>>()
  const [isSubstitutionFocused, setIsSubstitutionFocused] = useState(false)
  const [selectedSubstitutionKey, setSelectedSubstitutionKey] = useState<string | undefined>(
    undefined
  )
  const [substitutions, setSubstitutions] = useState(undefined)
  const [substitutionsFromPing, setSubstitutionsFromPing] = useState()
  const [productPreviewPayload, setProductPreviewPayload] = useState(null)
  const [hasError, setHasError] = useState(false)
  const [pageColors, setPageColors] = useState(null)
  const [templateJson, setTemplateJson] = useState<TPaywallTemplate | null>(null)
  const [hoverSubstitutionKey, setHoveredSubstitutionKey] = useState<string | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)

  const memoizedChildren = useMemo(() => children, [children])
  return (
    <PaywallContext.Provider
      value={{
        paywall,
        setQueryData,
        refetchPaywall: refetch,
        paywallId,
        isSubstitutionFocused,
        setIsSubstitutionFocused,
        selectedSubstitutionKey,
        setSelectedSubstitutionKey,
        substitutions,
        setSubstitutions,
        substitutionsFromPing,
        setSubstitutionsFromPing,
        productPreviewPayload,
        setProductPreviewPayload,
        hasError,
        setHasError,
        pageColors,
        setPageColors,
        templateJson,
        setTemplateJson,
        iframeRef,
        setIframeRef,
        hoverSubstitutionKey,
        setHoveredSubstitutionKey,
        isInitializing,
        setIsInitializing,
      }}
    >
      {memoizedChildren}
    </PaywallContext.Provider>
  )
}

const usePaywallContext = () => useContext(PaywallContext)

// Naive implementation of a selector hook
export const naiveUseSpecificPaywallContext = <K extends keyof PaywallContextType>(
  selectors: Selector<K>
): Pick<PaywallContextType, K> => {
  const context = usePaywallContext()
  const selectedContext = {} as Pick<PaywallContextType, K>

  Object.keys(selectors).forEach((key) => {
    // @ts-ignore
    selectedContext[key] = context[key]
  })

  // return selectedContext
  return selectedContext
}

export const memoizedUseSpecificPaywallContext = <K extends keyof PaywallContextType>(
  selectors: Selector<K>
): Pick<PaywallContextType, K> => {
  const context = usePaywallContext()

  const [revision, setRevision] = useState(0) // An internal state to trigger updates.
  const selectedContext = {} as Pick<PaywallContextType, K>

  Object.keys(selectors).forEach((key) => {
    // @ts-ignore
    selectedContext[key] = context[key]
  })

  const previousValueRef = useRef<Pick<PaywallContextType, K>>(selectedContext)

  useEffect(() => {
    let hasChanged = false

    Object.keys(selectors).forEach((key) => {
      if (previousValueRef.current && previousValueRef.current[key] !== context[key]) {
        hasChanged = true
      }
    })

    if (hasChanged || !previousValueRef.current) {
      previousValueRef.current = selectedContext
      setRevision((prev) => prev + 1) // Increase the revision when the ref is updated.
    }
    // Warning: Do not update the dependency array, should only depend on the
    // conext and selectors.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, selectors])

  // Warning: Do not update the dependency array, should only depend on the
  // revision.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => previousValueRef.current!, [revision]) // Use the revision as the dependency.
}

export const useSpecificPaywallContext = naiveUseSpecificPaywallContext
